import React, { useState } from 'react'
import { useSetAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import { useDebounce } from '@node-space/hooks'
import type { ContactPaymentInstrument } from '@node-space/types'
import {
  Box,
  Button,
  EmptyState,
  Input,
  Lottie,
  RowsSkeletonLoader,
} from '@node-space/ui-components'
import NoSearchResults from 'components/NoSearchResults/NoSearchResults'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { useContactInstrumentsInfiniteQuery } from 'hooks/queries/useContactInstrumentsInfiniteQuery'
import {
  contactActionAtom,
  contactAtom,
  contactInstrumentActionAtom,
} from 'pages/Contacts/atoms/contactAtoms'
import { ContactsSheets } from 'pages/Contacts/components/ContactsSheets'
import { ContactInstrumentsSheet } from 'pages/Contacts/ContactInstruments'
import { stripSpecialCharacters } from 'pages/Contacts/utils/utils'
import { ContactActionIds, ContactInstrumentActionIds } from 'types/atoms/atom.contacts'
import { Contact, InstrumentType } from 'types/contacts'
import { ContactInstrumentsList } from './ContactInstrumentsList'

interface ContactSelectorProps {
  instrumentType?: InstrumentType
  onSelect: (contactPayment: ContactPaymentInstrument) => void
}

export const ContactSelector = ({
  instrumentType = InstrumentType.CRYPTO,
  onSelect,
}: ContactSelectorProps) => {
  const { t } = useTranslation()
  const [searchQuery, setSearchQuery] = useState('')
  const [hasLoadedMore, setHasLoadedMore] = useState(false)
  const [isContactSheetOpen, setIsContactSheetOpen] = useState(false)
  const [isInstrumentSheetOpen, setIsInstrumentSheetOpen] = useState(false)
  const debouncedSearchTerm = useDebounce(searchQuery, 300)

  const setContact = useSetAtom(contactAtom)
  const setContactAction = useSetAtom(contactActionAtom)
  const setContactInstrumentAction = useSetAtom(contactInstrumentActionAtom)

  const {
    data: contactInstruments,
    fetchNextPage,
    hasNextPage,
    isError,
    isPending,
    isFetchingNextPage,
  } = useContactInstrumentsInfiniteQuery({
    searchTerm: debouncedSearchTerm,
    paymentMethod: instrumentType,
  })

  const hasContacts = !!contactInstruments?.length
  const noResults = !!debouncedSearchTerm && !hasContacts
  const showErrorState = isError && !isPending
  const showEmptyState = !isError && !isPending && !hasContacts && !debouncedSearchTerm
  const showSearch = hasContacts || noResults
  const showNoSearchResults = noResults && !isPending

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(stripSpecialCharacters(event.target.value))
  }

  const handleClearSearch = () => {
    setSearchQuery('')
  }

  const handleContactSelect = (contactPayment: ContactPaymentInstrument) => {
    if (contactPayment) {
      onSelect(contactPayment)
    }
  }

  const handleOpenContactSheet = () => {
    setContactAction(prev => ({
      ...prev,
      actionId: ContactActionIds.CONTACT,
      header: t('contacts.addContact'),
      onClose: () => {
        setIsContactSheetOpen(false)
        setContact({} as Contact)
      },
      meta: {
        enableCreateInstrument: true,
        flowType: instrumentType,
      },
    }))
    setIsContactSheetOpen(true)
  }

  const handleOpenInstrumentSheet = () => {
    setContact({} as Contact)
    setContactInstrumentAction(prev => ({
      ...prev,
      actionId: ContactInstrumentActionIds.CONTACT_INSTRUMENT_DECISION,
      onClose: () => {
        setIsInstrumentSheetOpen(false)
        setContact({} as Contact)
      },
    }))
    setIsInstrumentSheetOpen(true)
  }

  return (
    <>
      {showErrorState && (
        <EmptyState
          testId="send-contact-error"
          LottiePlayer={<Lottie src="connection-error" />}
          title={t('defaultError')}
          description={
            <>
              {`${t('requestErrors.comeBackLater')} `}
              <Button variant="ghost" noPadding onClick={() => globalThis.location.reload()}>
                {t('requestErrors.refreshPage')}
              </Button>
            </>
          }
        />
      )}

      {!isError && (
        <>
          {showSearch && (
            <Box paddingB={16} width="full" justify="between" flex gap={12}>
              <Box background="standard" width="full">
                <Input
                  value={searchQuery}
                  placeholderText={t('contacts.searchPlaceholder')}
                  onChange={handleSearchChange}
                  searchable
                  testId="contact-search-field"
                />
              </Box>
              <Button testId="add-contact" variant="secondary" onClick={handleOpenInstrumentSheet}>
                {t('contacts.addNew')}
              </Button>
            </Box>
          )}

          {isPending && <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />}

          {showNoSearchResults && <NoSearchResults clearSearchCriteria={handleClearSearch} />}

          {hasContacts && (
            <Box className="w-full">
              <ContactInstrumentsList
                contacts={contactInstruments}
                isFetchingNextPage={isFetchingNextPage}
                hasLoadedMore={hasLoadedMore}
                hasNextPage={hasNextPage ?? false}
                handleContactSelect={handleContactSelect}
                setHasLoadedMore={setHasLoadedMore}
                fetchNextPage={fetchNextPage}
              />
            </Box>
          )}
        </>
      )}

      {showEmptyState && (
        <EmptyState
          testId="send-contact-empty"
          LottiePlayer={<Lottie src="no-data" />}
          title={t('contacts.noContactHeadline')}
          description={t('contacts.addContactDescription')}
          PrimaryButton={
            <Button testId="add-contact" onClick={handleOpenContactSheet}>
              {t('contacts.addContact')}
            </Button>
          }
        />
      )}

      <ContactsSheets isSheetOpen={isContactSheetOpen} isNestedSheet />
      <ContactInstrumentsSheet isSheetOpen={isInstrumentSheetOpen} isNestedSheet />
    </>
  )
}
